import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ChecklistVerdictCard } from '@/components/molecules/ChecklistVerdictCard';
import { ExternalEvaluationCard } from '@/components/molecules/ExternalEvaluationCard';
import { RubricVerdictCard } from '@/components/molecules/RubricVerdictCard';
import { useConversationEvaluations, useRunEvaluations } from '@/services/Evaluations';

import type { EvaluationsPanelProps } from './EvaluationsPanel.types';

const formatTime = (iso: string): string => {
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const EvaluationsPanel = ({ threadId }: EvaluationsPanelProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const { data, isLoading } = useConversationEvaluations(threadId);
	const runMutation = useRunEvaluations();

	const handleRun = () => runMutation.mutate({ threadId });

	const items = data?.items ?? [];
	const lastRunAt = items[0]?.runAt;
	const lastDuration = items[0]?.durationMs;

	return (
		<section className="border-t border-subtle pt-md" aria-label={t('EVALUATIONS_TITLE')}>
			<header className="mb-md flex items-baseline justify-between">
				<div>
					<h2 className="text-[14px] font-semibold text-default">{t('EVALUATIONS_TITLE')}</h2>
					<p className="mt-3xs text-[12px] text-subtle">{t('EVALUATIONS_SUB', { count: items.length })}</p>
				</div>
				<button
					type="button"
					onClick={handleRun}
					disabled={runMutation.isPending || (data?.totalApplicable ?? 0) === 0}
					className="inline-flex items-center gap-3xs rounded-md border border-subtle bg-white px-xs py-3xs text-[11px] text-default transition-colors hover:bg-grey-25 disabled:cursor-not-allowed disabled:opacity-50"
				>
					<RefreshCw className={`h-sm w-sm ${runMutation.isPending ? 'animate-spin' : ''}`} />
					{runMutation.isPending ? t('EVALUATIONS_RUNNING') : t('EVALUATIONS_RUN')}
				</button>
			</header>

			{isLoading ? (
				<div className="rounded-md bg-grey-25 px-md py-md text-[12px] text-subtle">…</div>
			) : data?.status === 'pending' ? (
				<p className="rounded-md bg-grey-25 px-md py-md text-[12px] text-subtle">{t('EVALUATIONS_PENDING')}</p>
			) : items.length === 0 ? (
				<p className="rounded-md bg-grey-25 px-md py-md text-[12px] text-subtle">{t('EVALUATIONS_EMPTY')}</p>
			) : (
				<>
					<div className="grid grid-cols-1 gap-md md:grid-cols-2">
						{items.map((v) => {
							if (v.kind === 'rubric') return <RubricVerdictCard key={`${v.judgeId}-${v.runAt}`} verdict={v} />;
							if (v.kind === 'checklist') return <ChecklistVerdictCard key={`${v.judgeId}-${v.runAt}`} verdict={v} />;

							return <ExternalEvaluationCard key={`${v.judgeId}-${v.runAt}`} verdict={v} />;
						})}
					</div>
					{lastRunAt && (
						<p className="mt-xs text-[11px] tabular-nums text-subtle">
							{t('EVALUATIONS_RUN_AT', { time: formatTime(lastRunAt), duration: lastDuration })}
						</p>
					)}
				</>
			)}
		</section>
	);
};
